import { SearchAccordion } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import {
  useLastWorkflowSearchHistory,
  useWorkflowSearchAccordionOpenSetting,
} from '@/features/workflow/hooks/local-storage';
import { useWorkflowListSearchQueryStates } from '@/features/workflow/hooks/search-query-states';
import { WorkflowSearchFormSchema } from '@/features/workflow/models/search';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';
import { WorkflowListSearchFilterTags } from './WorkflowListSearchFilterTags';
import { WorkflowListSearchForm } from './WorkflowListSearchForm';

type FormValues = z.infer<typeof WorkflowSearchFormSchema>;

export const WorkflowListSearchAccordion: FC = () => {
  const { data: me } = useMe();
  const {
    workflowSearchAccordionOpenSetting,
    setWorkflowSearchAccordionOpenSetting,
  } = useWorkflowSearchAccordionOpenSetting(me?.id ?? '');
  const [searchQueryStates, setSearchQueryStates] =
    useWorkflowListSearchQueryStates();

  const { setSearchHistoryStorage } = useLastWorkflowSearchHistory();

  const defaultValues = useMemo(() => {
    return {
      createdMonthRange: [
        searchQueryStates.createdAtFrom
          ? dayjs(searchQueryStates.createdAtFrom)
          : undefined,
        searchQueryStates.createdAtTo
          ? dayjs(searchQueryStates.createdAtTo)
          : undefined,
      ],
      actionRequired: searchQueryStates.actionRequired,
      keyword: searchQueryStates.keyword ?? null,
      stageLabelId: searchQueryStates.stageLabelId ?? null,
      tagIds:
        searchQueryStates.tagIds?.at(0) === 'null'
          ? []
          : searchQueryStates.tagIds,
      workflowStatus: searchQueryStates.workflowStatus,
    } as const satisfies FormValues;
  }, [searchQueryStates]);

  const formMethods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(WorkflowSearchFormSchema),
  });

  const { reset } = formMethods;

  const handleSearch = useCallback(
    async ({ createdMonthRange, tagIds, ...otherParams }: FormValues) => {
      const createdAtFrom = createdMonthRange?.[0]?.format('YYYY-MM-DD');
      const createdAtTo = createdMonthRange?.[1]?.format('YYYY-MM-DD');

      const nextSearchQueryStates = {
        ...otherParams,
        createdAtFrom: createdAtFrom ?? null,
        createdAtTo: createdAtTo ?? null,
        tagIds,
        page: 1,
      };
      // localStorageに保存するため
      const parsedRecord = JSON.parse(JSON.stringify(nextSearchQueryStates));

      setSearchHistoryStorage(parsedRecord);
      setSearchQueryStates(nextSearchQueryStates);
    },
    [setSearchHistoryStorage, setSearchQueryStates],
  );

  // localStorageの値を反映させるため
  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <FormProvider {...formMethods}>
      <SearchAccordion
        defaultOpen={workflowSearchAccordionOpenSetting.workflow}
        onChange={(open) => {
          setWorkflowSearchAccordionOpenSetting({
            ...workflowSearchAccordionOpenSetting,
            workflow: open,
          });
        }}
        filterTags={
          <div>
            <WorkflowListSearchFilterTags />
          </div>
        }
        searchForm={
          <WorkflowListSearchForm
            onSearch={handleSearch}
            resetFormValues={{
              createdMonthRange: null,
              stageLabelId: null,
              tagIds: [],
              workflowStatus: 'all',
              actionRequired: 'all',
              keyword: null,
            }}
          />
        }
      />
    </FormProvider>
  );
};
